import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Chip, Button, TextField,
  InputAdornment, Avatar, Skeleton, Alert, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Search, School, Star, Person, CalendarMonth, Add } from '@mui/icons-material';
import knowledgeService, { KnowledgeSession, Eligibility } from '../../services/knowledgeService';
import eventsService from '../../services/eventsService';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../theme/theme';

const TOPIC_TAGS = [
  'Engineering', 'Design', 'Marketing', 'Finance', 'Startups', 'AI/ML',
  'Product', 'Leadership', 'Cloud/DevOps', 'Data Science', 'Cybersecurity', 'Blockchain',
];

export default function KnowledgeBrowsePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [sessions, setSessions] = useState<KnowledgeSession[]>([]);
  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [filterZone, setFilterZone] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'masterclass' | 'knowledge_session'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [knowledgeSessions, masterclassEvents, z] = await Promise.all([
          knowledgeService.browse(),
          eventsService.getWeekly({ event_type: 'masterclass' }),
          eventsService.getZones(),
        ]);
        // Merge: convert masterclass events to session-like format
        const masterclassAsSessions = (masterclassEvents || []).map((e: any) => ({
          id: e.id, title: e.title, description: e.description,
          scheduled_date: e.scheduled_date, scheduled_time: e.scheduled_time,
          zone_id: e.zone?.id || e.zone_id, venue_name: e.venue?.name,
          professional_name: 'Master Class', topic_tags: [],
          knowledge_fee: e.masterclass_fee || 0, base_price: e.price || 10,
          current_count: e.current_count, max_capacity: e.max_capacity,
          event_type: 'masterclass',
        }));
        // Tag knowledge sessions with event_type
        const taggedKnowledge = knowledgeSessions.map((s: any) => ({ ...s, event_type: s.event_type || 'knowledge_session' }));
        // Combine and deduplicate by id
        const combined = [...taggedKnowledge, ...masterclassAsSessions];
        const unique = combined.filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i);
        setSessions(unique);
        setZones(z);
        if (isAuthenticated) {
          try {
            const e = await knowledgeService.checkEligibility();
            setEligibility(e);
          } catch (err) {
            // expected: eligibility is optional — banner is informational only.
            console.warn('[KnowledgeBrowsePage] eligibility check failed', err);
          }
        }
      } catch (err) {
        // Background load — empty state is shown to user; log for observability.
        console.warn('[KnowledgeBrowsePage] sessions load failed', err);
      }
      setLoading(false);
    };
    load();
  }, [isAuthenticated]);

  const filtered = sessions.filter(s => {
    if (filterType !== 'all' && (s as any).event_type !== filterType) return false;
    if (filterZone && s.zone_id !== filterZone) return false;
    if (filterTopic && !s.topic_tags?.includes(filterTopic)) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <School sx={{ fontSize: 32, color: colors.primary.main }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.neutral[800] }}>
            Learn
          </Typography>
        </Box>
        <Typography color="text.secondary">
          Learn from Baku's top professionals over coffee. Master classes, knowledge sessions, and hands-on workshops at partner cafes.
        </Typography>
      </Box>

      {/* Create CTA */}
      {isAuthenticated && eligibility?.eligible && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary.subtle, border: `1px solid ${colors.primary.light}`, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Share your expertise</Typography>
              <Typography variant="body2" color="text.secondary">You're eligible to create knowledge sessions</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/knowledge/create')}>
              Create Session
            </Button>
          </Box>
        </Paper>
      )}

      {isAuthenticated && eligibility && !eligibility.eligible && (
        <Alert severity="info" sx={{ mb: 3 }}>
          To create knowledge sessions, you need: 3+ events attended and 4.0+ avg rating.
          You have {eligibility.events_attended} events. Keep attending!
        </Alert>
      )}

      {/* Type filter tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {([
          { value: 'all', label: 'All Learning' },
          { value: 'masterclass', label: 'Master Classes' },
          { value: 'knowledge_session', label: 'Knowledge Sessions' },
        ] as const).map(t => (
          <Chip key={t.value} label={t.label} clickable
            onClick={() => setFilterType(t.value)}
            sx={{
              bgcolor: filterType === t.value ? colors.primary.main : colors.neutral[100],
              color: filterType === t.value ? 'white' : colors.neutral[700],
              fontWeight: 600,
            }} />
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search sessions..." value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Zone</InputLabel>
          <Select value={filterZone} label="Zone" onChange={e => setFilterZone(e.target.value)}>
            <MenuItem value="">All Zones</MenuItem>
            {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Topic</InputLabel>
          <Select value={filterTopic} label="Topic" onChange={e => setFilterTopic(e.target.value)}>
            <MenuItem value="">All Topics</MenuItem>
            {TOPIC_TAGS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Sessions List */}
      {loading ? (
        [1,2,3].map(i => <Skeleton key={i} height={160} sx={{ mb: 2, borderRadius: 3 }} />)
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <School sx={{ fontSize: 48, color: colors.neutral[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No knowledge sessions yet</Typography>
          <Typography variant="body2" color="text.secondary">Be the first to share your expertise!</Typography>
        </Paper>
      ) : (
        filtered.map(session => (
          <Paper
            key={session.id}
            sx={{
              p: 3, mb: 2, cursor: 'pointer', borderRadius: 3,
              border: '1px solid transparent',
              '&:hover': { border: `1px solid ${colors.primary.light}`, transform: 'translateY(-1px)' },
              transition: 'all 0.2s',
            }}
            onClick={() => navigate(`/events/${session.id}`)}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Professional avatar */}
              <Avatar
                sx={{ width: 56, height: 56, bgcolor: colors.primary.main, fontSize: 20 }}
                src={session.professional_avatar || undefined}
              >
                {session.professional_name?.[0] || '?'}
              </Avatar>

              {/* Content */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{session.title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: 16, color: colors.neutral[400] }} />
                    <Typography variant="body2" color="text.secondary">{session.professional_name}</Typography>
                  </Box>
                  {session.professional_rating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <Star sx={{ fontSize: 14, color: '#F2994A' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{session.professional_rating.toFixed(1)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonth sx={{ fontSize: 16, color: colors.neutral[400] }} />
                    <Typography variant="body2" color="text.secondary">{session.scheduled_date} at {session.scheduled_time?.slice(0,5)}</Typography>
                  </Box>
                </Box>

                {/* Topic tags */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {session.topic_tags?.map(t => (
                    <Chip key={t} label={t} size="small" sx={{ bgcolor: colors.neutral[100], fontSize: '0.7rem' }} />
                  ))}
                </Box>

                {/* Description preview */}
                <Typography variant="body2" color="text.secondary" sx={{
                  overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {session.description}
                </Typography>
              </Box>

              {/* Price & spots */}
              <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.primary.main }}>
                  {(session.knowledge_fee + (session.voucher_tier === 'premium' ? 13 : 10)).toFixed(0)} AZN
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  incl. coffee
                </Typography>
                <Typography variant="body2" sx={{
                  mt: 1, fontWeight: 600,
                  color: session.current_count >= session.max_capacity ? colors.error.main : colors.success.main,
                }}>
                  {session.current_count >= session.max_capacity
                    ? 'Full'
                    : `${session.max_capacity - session.current_count} spot${session.max_capacity - session.current_count > 1 ? 's' : ''} left`}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))
      )}
    </Container>
  );
}
